/** @jsx jsx */

import { Fragment, useState, ReactNode, useEffect } from 'react';

import { HeadingLevelProvider } from '@zapier/design-system';
import { NotFound } from '@zapier/common-components';
import { jsx } from '@emotion/core';

import Sidebar from 'app/developer-v3/components/Sidebar';
import {
  useAvroEvent,
  useConversion,
  useDismissable,
  useVersionLifecycle,
} from 'app/developer-v3/hooks';
import SidebarRight, {
  Provider as SidebarRightProvider,
  PropertiesType as SidebarRightPropertiesType,
} from 'app/developer-v3/components/SidebarRight';
import {
  MODES as SIDEBAR_RIGHT_MODES,
  TABS as SIDEBAR_RIGHT_TABS,
  VB_GUIDE_LOCAL_STORAGE_KEY,
  COMMON_SECTION_IDS,
} from 'app/developer-v3/components/SidebarRight/constants';
import { BASE_APP_URL } from 'app/developer-v3/constants';
import { getSlugOfMissingRequiredSettings } from 'app/developer-v3/utils';

import AlertMissingSettings from './AlertMissingSettings';
import CTA from './CTA';
import MainContent from './MainContent';
import Styles from './AppBuilder.styles';
import { useDelayedCall } from './useDelayedCall';

import type { AppSummary } from 'app/developer-v3/types';

type Props = {
  appSummary: AppSummary;
  children: ReactNode;
  hasFailure: boolean;
  hasMissingRequiredIntegrationSettings: boolean;
  isAppFound: boolean;
  isAppLoading: boolean;
  isGUIIntegrationOnlyRoute: boolean;
  onVersionChange: (versionNumber: string) => {};
  params: { id?: ID; version?: string };
  // Only for testing.
  pollDelay?: number;
};

/** The main component of this application. It wraps all routes that edit a
 * given integration. */
const AppBuilder = (props: Props) => {
  const {
    appSummary,
    children,
    hasFailure,
    hasMissingRequiredIntegrationSettings,
    isAppLoading,
    isAppFound,
    onVersionChange,
    params,
    pollDelay,
  } = props;

  const [sidebarRightProperties, setSidebarRightProperties] = useState<
    SidebarRightPropertiesType
  >({
    mode: SIDEBAR_RIGHT_MODES.DEFAULT,
    activeSubsection: COMMON_SECTION_IDS.NONE,
    tabActive: SIDEBAR_RIGHT_TABS.NONE,
    sectionContext: {},
  });

  const conversion = useConversion();
  const fetchWbMigrationStatus = conversion.fetchMigrationStatus;

  const lifecycle = useVersionLifecycle(appSummary.appId);
  const loadLifecycleProgress = lifecycle.loadProgress;

  // Due to how redux-routing-kit is set up, you can manually construct
  // URLs that don't route to a specific component, which ends up setting
  // the `children` prop to null. Instead of crashing, we display a 404 page!
  const hasChildren = children !== null;

  const shouldGetConversionProgress =
    appSummary.wasConvertedFromWB &&
    !conversion.stage &&
    !conversion.isProgress.loading &&
    !conversion.isProgress.failure;
  useEffect(() => {
    if (shouldGetConversionProgress) {
      fetchWbMigrationStatus();
    }
  }, [fetchWbMigrationStatus, shouldGetConversionProgress]);

  useEffect(() => {
    loadLifecycleProgress();
  }, [loadLifecycleProgress]);

  useDelayedCall({
    callback: fetchWbMigrationStatus,
    shouldCall: conversion.isMigrating && !conversion.isProgress.loading,
    delay: pollDelay,
  });

  useDelayedCall({
    callback: loadLifecycleProgress,
    shouldCall: lifecycle.isPending && !lifecycle.isLoading,
    delay: pollDelay,
  });

  const updateSidebarRight = properties => {
    setSidebarRightProperties({ ...sidebarRightProperties, ...properties });
    localStorage.setItem(VB_GUIDE_LOCAL_STORAGE_KEY, properties.tabActive);
  };

  const mainContent = (
    <HeadingLevelProvider level={1}>
      <MainContent
        appSummary={appSummary}
        hasFailure={hasFailure}
        onVersionChange={onVersionChange}
        params={params}
      >
        {children}
      </MainContent>
    </HeadingLevelProvider>
  );

  const notificationId = getSlugOfMissingRequiredSettings(appSummary.appId);
  const { dismiss, isVisible } = useDismissable({ persistId: notificationId });
  const { emitPartnerUserInteractionEvent } = useAvroEvent();
  const [eventEmitted, setEventEmitted] = useState(false);

  const shouldShowAlertMissingSettings =
    !isAppLoading &&
    !appSummary.isPublished &&
    isVisible &&
    hasMissingRequiredIntegrationSettings;

  useEffect(() => {
    if (shouldShowAlertMissingSettings && !eventEmitted) {
      emitPartnerUserInteractionEvent({
        eventAction: 'in_view',
        interactionGoal: 'Nudge to update integration settings',
        interactionName: 'update_integration_settings_prompt_displayed',
      });
      setEventEmitted(true);
    }
  }, [
    eventEmitted,
    emitPartnerUserInteractionEvent,
    shouldShowAlertMissingSettings,
  ]);

  return (isAppLoading || isAppFound) && hasChildren ? (
    <SidebarRightProvider
      activeSubsection={sidebarRightProperties.activeSubsection}
      mode={sidebarRightProperties.mode}
      sectionContext={sidebarRightProperties.sectionContext}
      tabActive={sidebarRightProperties.tabActive}
      update={updateSidebarRight}
    >
      <Fragment>
        {shouldShowAlertMissingSettings && (
          <AlertMissingSettings
            integrationId={appSummary.appId}
            onDismiss={dismiss}
          />
        )}
        <CTA
          integrationId={appSummary.appId}
          pollDelay={pollDelay}
          wasConvertedFromWB={appSummary.wasConvertedFromWB}
        />
        <div className="app-builder" css={Styles.root}>
          {appSummary ? (
            <Fragment>
              <Sidebar
                appSummary={appSummary}
                onVersionChange={onVersionChange}
              />
              <SidebarRight
                appId={appSummary.appId}
                isPublished={appSummary.isPublished}
                mode={sidebarRightProperties.mode}
                sectionContext={sidebarRightProperties.sectionContext}
                tabActive={sidebarRightProperties.tabActive}
                update={updateSidebarRight}
                version={appSummary.version || '1.0.0'}
                versionStatus={appSummary.versionStatus}
              >
                {mainContent}
              </SidebarRight>
            </Fragment>
          ) : (
            mainContent
          )}
        </div>
      </Fragment>
    </SidebarRightProvider>
  ) : (
    <div css={{ maxHeight: 900 }}>
      <NotFound safetyRoute={`${BASE_APP_URL}/`} />
    </div>
  );
};

export default AppBuilder;
